import './button.css';

import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ComponentPropsWithoutRef, ComponentType } from 'react';

import { Icon } from '../Icon';
import { Loader, LoaderProps } from '../Loader';

const LOADER_VARIANT_BY_BUTTON_VARIANT: Record<string, LoaderProps['variant']> = {
    ghost: 'default',
    ghostDestructive: 'destructive',
};

const buttonVariants = cva(
    [
        'button font-dynamic lg-font-dynamic',
        'tw-flex tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap',
        'focus-visible:!tw-outline-none focus-visible:!tw-border-solid focus-visible:!tw-border-2 focus-visible:!tw-border-primary-focus',
    ],
    {
        variants: {
            variant: {
                default: 'default-button',
                secondary: 'secondary-button',
                destructive: 'destructive-button',
                outline: 'outline-button',
                ghost: 'ghost-button',
                ghostDestructive: 'ghost-destructive-button',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

type ButtonIconComponent = ComponentType<
    ComponentPropsWithoutRef<'svg'> & { title?: string }
>;

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    loading?: boolean;
    /** When true, renders the provided `icon` before the button label. */
    hasIcon?: boolean;
    /** Icon component rendered when `hasIcon` is true. */
    icon?: ButtonIconComponent;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
    children,
    className,
    variant,
    hasIcon = false,
    icon,
    loading,
    disabled,
    type,
    ...props
}, ref) => {
    const loaderVariant = (variant && LOADER_VARIANT_BY_BUTTON_VARIANT[variant])
        ?? (variant as LoaderProps['variant']);
    return (
        <button
            ref={ref}
            // Default to `type="button"` so the Enter key reliably triggers
            // `onClick` and does not accidentally submit an ancestor <form>.
            type={type ?? 'button'}
            className={buttonVariants({ variant, className })}
            disabled={disabled}
            {...props}
        >
            {loading ? <Loader variant={loaderVariant} className="tw-mx-3" /> : null}
            {hasIcon && icon ? <Icon icon={icon} className="tw-aspect-square tw-w-5 lg:tw-w-6" /> : null}
            {children}
        </button>
    );
});

Button.displayName = 'Button';

export { Button, type buttonVariants as ButtonVariants };
